/**
 * What `public.outbox_payload(id)` hands a template.
 *
 * One shape for all fifty codes of §11.5, with the parts a given code does
 * not use left null. The division that matters is `audience`: a `customer`
 * payload carries the Össze­sítés of §5.11 and nothing the constraints keep
 * off the page — no production unit, no minimum batch, no margin, no capacity
 * figure and no review reason. `internal` is the only payload that carries
 * `internal`, and the database, not a template, decides which is built.
 */

export type Audience = 'customer' | 'internal'

export type PriceCategory = 'sos' | 'edes' | 'gyumolcs' | 'ital'

export type ServiceCategory = 'szallitas' | 'felszereles' | 'szemelyzet'

export interface CategoryTotal {
  quantity: number
  unit: string | null
  net: number
  vat: number
  gross: number
}

export interface OrderLine {
  dish_id: string
  name_hu: string | null
  name_en: string | null
  price_category: PriceCategory
  is_gf_portion: boolean
  /**
   * Run 17 item B5(b): how many of `quantity` belong to the gluten-free
   * guests. A gluten-free dish already on the menu carries their share
   * inside its own quantity rather than in a second line; the kitchen packs
   * this many into the labelled gluten-free box. Absent on a payload written
   * before 15 October 2026, where the share is a whole `is_gf_portion` line.
   */
  gf_portions?: number | null
  is_drink: boolean
  quantity: number
  unit: string | null
  /** §14: allergens always as code and full name. The codes travel on the
   *  line snapshot (§19.2 principle 1), never out of the live catalogue. */
  allergens: { code: string | number; name_hu?: string | null }[] | null
  /** §7.3. `verified` only once Robi has signed the dish off; anything else
   *  prints as example data and must never read as verified. */
  allergen_status: string | null
  allergen_test_marked: boolean | null
  gluten_free_eligible: boolean | null
  /** Run 10 item 7b: the publication contract's `plant_based_lookalike`. A
   *  customer document prints the name in italics with an asterisk and one
   *  footnote; a kitchen document ignores it. Absent on older payloads. */
  plant_based_lookalike?: boolean | null
  /** Run 10 item 7c: the flag per language, since the terms that find it are
   *  per language. A customer email prints `name_hu`, so it reads `_hu`, and
   *  the run 10 key where a payload is older. */
  plant_based_lookalike_hu?: boolean | null
  plant_based_lookalike_en?: boolean | null
  /** Run 12 item F: where the printed name is marked, per language,
   *  0-based [start, end) into name_hu / name_en. */
  name_hu_marks?: [number, number][] | null
  name_en_marks?: [number, number][] | null
  /** Run 11 item 3: the description the page shows, per language. No email
   *  prints one today; run 12 item 8 makes sure one never prints the other
   *  language's. */
  shown_description_hu?: string | null
  shown_description_en?: string | null
  /** Internal payloads only (§11.6 K1/K2). Null on a customer payload. */
  station: string | null
  production_units: number | null
  production_day: string | null
}

export interface OrderPayload {
  ref: string
  /** SimplePay test prompt §2: `orders.is_test`. */
  is_test?: boolean
  status: string
  status_reason: string | null
  lane: 'card' | 'transfer' | 'short_notice'
  lang: string
  is_business: boolean
  version: number
  event_name: string | null
  event_date: string
  event_start: string | null
  occasion: string | null
  occasion_label: string | null
  /** Run 12 item B: the occasion in English, for an English email. */
  occasion_label_en?: string | null
  /**
   * **Run 19 §4 (DECIDED 20261018, Barna): the meal note.** The sentence
   * that says whether this order's food replaces a meal, in the order's own
   * language and in English, exactly as `public.meal_replacement_note`
   * writes it. `outbox_order_json` has carried both since run 17 item
   * B11(c); run 19 is where the templates print them.
   *
   * Optional, because an outbox row written before run 17 has neither, and
   * null for the occasions that carry no note at all — *Munkaebéd*,
   * *Kávészünet* and *Egyéb*. Nothing here writes the sentence: a template
   * that cannot find it prints the occasion alone rather than inventing one.
   */
  meal_note?: string | null
  meal_note_en?: string | null
  guests: number
  gf_guests: number
  fulfilment: 'delivery' | 'pickup'
  slot_id: string | null
  slot_label: string | null
  /**
   * Run 16 item 2: the day the food changes hands — the event date for a
   * delivery and for an ordinary collection, the business day before for a
   * weekend event (§8.1). Optional, because an outbox row written before this
   * field existed has none; the renderer falls back to `event_date`.
   */
  fulfilment_date?: string | null
  zone: string | null
  /**
   * Specification §3.7: the delivery postcode's straight-line distance from
   * Graphisoft Park, next to the zone. On the internal payload only.
   */
  distance_km?: number | string | null
  postcode: string | null
  delivery_address: Record<string, string> | null
  customer_name: string | null
  customer_email: string | null
  customer_company: string | null
  net: number
  vat: number
  gross: number
  vat_pct: number
  delivery_fee: number
  totals_by_category: Partial<Record<PriceCategory | ServiceCategory, CategoryTotal>>
  payment_deadline: string | null
  freeze_at: string | null
  /**
   * `build/FIXES.md` item 79 (F8): by when a confirmed offer has to be paid.
   * Since run 12 item 5: `payment_window_bd` business days after the approval,
   * capped at the order deadline (transfer, through transfer_payment_deadline)
   * or the freeze point (card). The 48-hour confirm_pay_window_h is gone. Null on every order that has not been through the
   * confirmation lane, which is every order rev7 describes.
   */
  confirm_pay_deadline: string | null
  created_at: string
  /** §5.11: the page says a colleague will approve it, and says no more. */
  in_review: boolean
  public_url: string | null
  /** Legal pages brief, item 2: the ÁSZF and privacy notice versions
   *  (`YYYYMMDD`) in force when the order was submitted. Null on an order
   *  placed before the documents existed. */
  terms_version?: string | null
  privacy_version?: string | null
  /** Item 5: set by the sender when U5 carries the ÁSZF PDF of
   *  `terms_version`; never set by the database. */
  terms_pdf_attached?: boolean
  lines: OrderLine[]
}

export interface InternalPayload {
  review_reasons: string[]
  review_state: string | null
  dietary_note: string | null
  /**
   * `build/FIXES.md` item 79 (F8): "The staff notification reuses C2, with
   * the comment text and the reasons."
   *
   * **Internal only, and that is the rule rather than the habit.** The
   * comment is free text from the customer and may contain dietary detail; it
   * is shown in the staff notification and the console and nowhere else. It
   * is on this interface and not on `OrderPayload` because a kitchen view, a
   * label and an invoice all read the order and none of them reads this.
   */
  confirmation_reasons: string[]
  customer_comment: string | null
  customer_phone: string | null
  is_late: boolean
  is_short_notice: boolean
  labels_blocked: boolean
  catalogue_version: string | null
  ruleset_version: number | null
  console_url: string | null
}

export interface InvoicePayload {
  /** build/FIXES.md item 88: `elolegszamla` and `vegszamla` join the four. */
  kind: 'dijbekero' | 'szamla' | 'elolegszamla' | 'vegszamla' | 'storno' | 'fee'
  szamlazz_number: string | null
  net: number
  vat: number
  gross: number
  issued_at: string | null
  /** `queued` · `issued` · `failed` · `held` (a végszámla, item 86). */
  status: string
  pdf_path: string | null
  /** On an INTERNAL payload only (item 86, for F8). A customer payload never
   *  carries them: `error` holds the Agent's own messages and, on a végszámla
   *  issued by hand, a staff address. */
  version?: number
  error?: string | null
  reversed_at?: string | null
  settles?: string[]
}

export interface PaymentPayload {
  kind: string
  provider: string
  provider_ref: string | null
  provider_txn_id: string | null
  amount: number
  currency: string
  status: string
  due_at: string | null
  done_at: string | null
  note: string | null
  /** Card payment change 4 (20261101): `transfer` or `card_dijbekero` on money
   *  received against a díjbekérő; absent on older payloads. */
  method?: string | null
}

export interface PendingChangePayload {
  proposed_version: number
  amount_due: number
  payment_ref: string | null
  expires_at: string | null
  status: string
  reason: string | null
}

export interface FeePayload {
  /** §9.7: base, percentage, fee, what was paid and the balance. */
  base_net: number
  pct: number
  /** The fee's **net**: the invoice line of §9.7, whatever the buyer type. */
  amount: number
  paid: number
  balance: number
  waived: boolean
  note: string | null
  /**
   * Barna's ruling 8 of 5 October 2026: which amount this buyer is quoted on.
   * `gross` for a consumer — the order's végösszeg, the figure the page showed
   * them — and `net` for a business buyer, whose invoice adds VAT to every
   * line. `public.fee_base_kind()` decides it; nothing here does.
   */
  base_kind: 'net' | 'gross'
  /** The base itself, in the buyer's own terms. */
  base: number
  /** What the buyer is charged. The same figure on either base, which is
   *  what ruling 8 means by "the charged amount is the same either way". */
  amount_gross: number
}

export interface EnquiryPayload {
  id: string
  name: string | null
  email: string | null
  phone: string | null
  message: string | null
  reasons: string[]
  created_at: string
}

export interface CallbackPayload {
  id: string
  name: string | null
  phone: string | null
  email: string | null
  expires_at: string | null
  created_at: string
}

export interface BankPayload {
  name: string | null
  account: string | null
}

/** The transfer details, the terms and the two addresses are not invented
 *  here: an empty string is a gap the template prints as a gap. */
export interface SitePayload {
  site_url: string | null
  /** Run 12 item G: the console's base address (`…/staff`), which the worker
   *  builds from its own `PUBLIC_SITE_URL`, on staff payloads only. A
   *  customer payload never carries it. */
  console_url?: string | null
  catering_email: string | null
  catering_phone: string | null
  reply_to: string | null
  /** Run 12 item 2c: From, and its name in the message's language, from the
   *  register (email_sender_address, email_sender_name_hu / _en). */
  sender_email?: string | null
  sender_name?: string | null
  /**
   * Run 16 item 6: the domain a **test** project may send a customer email
   * to, as the register holds it (`test_email_domain`). Read by the worker
   * only when `APP_ENV` is not production; the production path never looks at
   * it and never adds the `[TESZT] ` subject prefix.
   */
  test_email_domain?: string
  /** Run 12 item 2b: email_allowed_recipients, as the register holds it. */
  allowed_recipients?: string | null
  bank: BankPayload
}

export const FEE_TERM_KEYS = [
  'fee_mod_after_freeze_pct',
  'fee_cancel_after_freeze_pct',
  'fee_within_window_pct',
  'fee_window_h',
  'notice_bd',
  'freeze_time',
] as const
export type FeeTermKey = (typeof FEE_TERM_KEYS)[number]

export interface EmailPayload {
  code: string
  outbox_id: number
  audience: Audience
  lang: string
  recipient_role: string | null
  to_email: string | null
  for_date: string | null
  created_at: string
  /** Run 12 item G: the outbox row's order, for the staff emails' console
   *  link. The ID only: never the reference or anything else off the order. */
  order_id?: string | null
  order: OrderPayload | null
  internal: InternalPayload | null
  invoices: InvoicePayload[]
  payments: PaymentPayload[]
  pending_change: PendingChangePayload | null
  fee: FeePayload | null
  enquiry: EnquiryPayload | null
  callback: CallbackPayload | null
  site: SitePayload
  /**
   * Run 11 item 6b: the register's fee terms, as `public.params` holds them
   * when the email is built — fee_mod_after_freeze_pct,
   * fee_cancel_after_freeze_pct, fee_within_window_pct, fee_window_h,
   * notice_bd, freeze_time. A key the register lacks is absent.
   */
  terms?: Partial<Record<FeeTermKey | 'card_payment_route', string>>
  /** Figures a digest or reminder code carries instead of one order
   *  (C10, C11, K5, K6, K7, F7, A1, A2). Shape is the code's own. */
  digest: Record<string, unknown> | null
  /** SimplePay test prompt §2: the outbox row's order is a test order. */
  is_test?: boolean
}

/**
 * redesign_payment_photos, card payment change 3 (DECIDED 20261101): while
 * `card_payment_route` is `dijbekero` the díjbekérő szamlazz.hu emails
 * carries a card payment link (Teya) beside the transfer details, and U2 and
 * U3 say so. A payload without the key is on the seeded default, dijbekero;
 * `simplepay` keeps the transfer-only wording.
 */
export const cardLinkOnDijbekero = (payload: EmailPayload): boolean =>
  (payload.terms?.card_payment_route ?? 'dijbekero').trim() !== 'simplepay'
